import { useRef, useState, useEffect } from "react";
import { TESTIDS } from "@/constants/testIds";
import { cn } from "@/lib/utils";

/**
 * Motion overlay for the Tsoro board. Adds a tactile "scoop + flick" gesture
 * on top of the existing click handler:
 *   - Press a legal pit, see the seeds visually lift toward your cursor/finger.
 *   - Release anywhere → the move from that pit is played.
 *   - A simple click still plays the move (motion is additive, not required).
 *
 * The component is rendered AS A SIBLING to <Board/> and uses pointer events
 * at the document level so the drag follows the cursor outside the pit.
 *
 * Props:
 *   active          – boolean (false disables the gesture)
 *   onPlay(row,col) – called when a drag is released from a legal pit
 *   legalSet        – Set of "r,c"
 *   containerRef    – ref to the board grid (to detect which pit the press
 *                      landed on via data-testid markers)
 */
export function MotionLayer({ active, legalSet, onPlay, containerRef }) {
  const [drag, setDrag] = useState(null); // {row,col,x,y,seeds}

  useEffect(() => {
    if (!active) return;
    const onDown = (e) => {
      if (e.target?.closest?.("button[data-testid^='pit-r']")) {
        const btn = e.target.closest("button[data-testid^='pit-r']");
        const row = Number(btn.dataset.row);
        const col = Number(btn.dataset.col);
        const count = Number(btn.dataset.count || 0);
        const isLegal = btn.dataset.legal === "true";
        if (!isLegal) return;
        // Begin drag — keep default so click can still fire on release at same spot.
        setDrag({ row, col, x: e.clientX, y: e.clientY, seeds: count, startX: e.clientX, startY: e.clientY });
      }
    };
    const onMove = (e) => {
      if (!drag) return;
      setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : null));
    };
    const onUp = (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const moved = Math.hypot(dx, dy) > 18;
      // If the user moved at all (drag-release), play the move from the original pit.
      // (A bare click is also handled by the Pit's onClick.)
      if (moved && legalSet.has(`${drag.row},${drag.col}`)) {
        onPlay(drag.row, drag.col);
      }
      setDrag(null);
    };

    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", () => setDrag(null));
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [active, drag, legalSet, onPlay]);

  if (!drag) return null;

  return (
    <div
      data-testid="motion-hand"
      className="fixed pointer-events-none z-50 -translate-x-1/2 -translate-y-1/2 select-none"
      style={{ left: drag.x, top: drag.y }}
    >
      <div
        className={cn(
          "w-14 h-14 rounded-full pit-base flex items-center justify-center",
          "shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
        )}
      >
        {/* Floating seed bunch */}
        <div className="relative w-9 h-9">
          {Array.from({ length: Math.min(drag.seeds, 5) }).map((_, i) => (
            <span
              key={i}
              className="absolute w-3 h-2.5 rounded-full seed-token"
              style={{
                left: `${20 + (i % 3) * 22}%`,
                top: `${15 + Math.floor(i / 3) * 35}%`,
                transform: `rotate(${(i * 53) % 90 - 45}deg)`,
              }}
            />
          ))}
        </div>
      </div>
      <div className="text-center mt-1 text-xs font-mono font-semibold bg-background/80 backdrop-blur rounded-full px-2 py-0.5 inline-block">
        {drag.seeds}
      </div>
    </div>
  );
}
